import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiCall from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import theme from '../../theme'; // Import theme directly for use with IoSettingsOutline
import useDeviceType from '../useDeviceType';

// STYLED COMPONENTS
import {
    ProfileContainer,
    BackButton as BackButtonStyled,
    ProfileHeader,
    ProfilePicture,
    Username,
    StatsContainer, // This is the top stats container from SocialProfile
    StatItem as SocialStatItem, 
    StatLabel as SocialStatLabel, 
    StatValue as SocialStatValue, 
    DomainIcon as SocialDomainIcon, 
    DomainCounter as SocialDomainCounter, 
    DomainDisplayWrapper as SocialDomainDisplayWrapper,
    VerticalSeparator,
    ButtonContainer,
    PrimaryActionButton,
    SecondaryActionButton,
    ButtonIcon,
    InfoSection, // This is the InfoSection from SocialProfile
    InfoBox as SocialInfoBox, // Renamed to avoid conflict if Profile/styles.js has InfoBox
    InfoIcon as InfoIconStyled, 
    InfoTextContainer as SocialInfoTextContainer, // Renamed
    InfoTitle as SocialInfoTitle, // Renamed
    InfoSubtitle as SocialInfoSubtitle, // Renamed
    LoadingErrorContainer,
    // Styled components for "Own Profile" content (to be defined in UserProfilePageStyles.js)
    RubriqueTitle,      // From Profile/styles.js
    RecapContainer,     // From Profile/styles.js
    StatBox,            // From Profile/styles.js (for Récap)
    StatNumber,         // From Profile/styles.js (for Récap)
    RecapStatLabel,          // From Profile/styles.js (for Récap) - formerly StatLabel
    SwipeSection,       // From Profile/styles.js
    SwipeBox,           // From Profile/styles.js
    QuizIcon,           // From Profile/styles.js
    SwipeText,          // From Profile/styles.js
    BoxContainer,       // Generic box from Profile/styles.js
    ItemBox,            // From Profile/styles.js
    ItemIcon,           // From Profile/styles.js
    ItemText,           // From Profile/styles.js
    ItemSeparator,      // From Profile/styles.js
    VoirPlusButton,     // From Profile/styles.js
    ScoreBox,           // From Profile/styles.js
    ScoreText,          // From Profile/styles.js
    PseudoTextArea,     // From Profile/styles.js
    ConfirmButton,      // From Profile/styles.js
    SliderContainer,    // From Profile/styles.js
    SliderValue,        // From Profile/styles.js
    GradeSlider,        // From Profile/styles.js
    ConfirmGradeButton, // From Profile/styles.js
    ErrorText,
    HeartIcon
} from './UserProfilePageStyles';

// ASSETS - Common & from SocialProfile
import backIcon from '../../assets/fleche_envoi_clair.png';
import defaultAvatar from '../../assets/default_picture.png';
import instaBleuIcon from '../../assets/insta_bleu.png';
import abonnementBlancIcon from '../../assets/abonnement_blanc.png';
import abonneIcon from '../../assets/abonné.png';
import locationIcon from '../../assets/localisation_suggestion.png';
import schoolIconAsset from '../../assets/lecole.png';
import defaultDomainIcon from '../../assets/mtiers.png';
import settingsGrisIcon from '../../assets/settings_gris.png';
import pseudoIconAsset from '../../assets/pseudo_icon.png';
import quizBuzzIconAsset from '../../assets/quiz_buzz_icon.png';
import coeurBlancIcon from '../../assets/coeur_blanc.png';

// ASSETS - Specific to UserProfilePage
import secondeIcon from '../../assets/top2_icon.png';
import premiereIcon from '../../assets/top2_icon.png';
import terminaleIcon from '../../assets/top_0.png';
import etudiantIcon from '../../assets/students-icon.png';
import parentIcon from '../../assets/family-icon.png';
import otherIcon from '../../assets/other-icon.png';
import top1Icon from '../../assets/top1_icon.png';
import top5Icon from '../../assets/5premiers.png';
import topClassIcon from '../../assets/classement-classe.png';
import general_icon from '../../assets/general_bac.png';
import tech_icon from '../../assets/tech_bac.png';
import pro_icon from '../../assets/professional_bac.png';
import artsIcon from '../../assets/arts_culture_icon.png';
import eppcsIcon from '../../assets/sport_icon.png';
import hggspIcon from '../../assets/hggsp_icon.jpeg';
import hlpIcon from '../../assets/hlp_icon.png';
import llcerIcon from '../../assets/llcer_icon.png';
import lcaIcon from '../../assets/lca_icon.png';
import mathIcon from '../../assets/math_icon.png';
import nsiIcon from '../../assets/informatique_icon.png';
import physChimIcon from '../../assets/physics_icon.png';
import svtIcon from '../../assets/svt_icon.png';
import siIcon from '../../assets/engineer_icon.png';
import sesIcon from '../../assets/ses_icon.png';
import bioEcoIcon from '../../assets/bioeco_icon.png';
import moyenne_croissance_icon from '../../assets/moyenne_croissante_icon.png';
import amcIcon from '../../assets/english_icon.png';

// ICONS from libraries

import { parseFormacodeData } from '../../utils/formaCodeUtils';
import { domainIconsFormacodesSousGrandDomaines, domainIconMapRaw } from '../Onboarding/domainIconMap.js';


// HELPER FUNCTIONS
const getBacLabel = (type) => {
  const bacLabels = { general: 'Général', technologique: 'Technologique', professionnel: 'Professionnel' };
  return bacLabels[type] || type;
};

const getSpecialityLabel = (type) => {
  const specialityLabels = {
    arts: 'Arts', eppcs: 'EPPCS', hggsp: 'HGGSP', hlp: 'HLP', llcer: 'LLCER',
    lca: "LLCA", math: 'Mathématiques', nsi: 'NSI', physchim: 'Physique-Chimie',
    svt: 'SVT', si: "SI", ses: 'SES', bioeco: 'Biologie-Ecologie',
    general: 'Moyenne générale', amc: 'Anglais Monde Contemporain'
  };
  return specialityLabels[type] || type;
};

const getUserTypeLabel = (type) => {
  const userLabels = {
    seconde: 'Seconde', 
    premiere: 'Première', 
    terminale: 'Terminale',
    etudiant: 'Étudiant', 
    parent: 'Parent', 
    autre: 'Autre',
    top1: 'Premier de la classe', 
    top5: 'Dans les 5 premiers', 
    top_class: 'Autre / Je ne sais pas',
    top_classe: 'Autre / Je ne sais pas',
  };
  return userLabels[type] || type;
};

const getIconForType = (type, category) => {
  if (!type) return null;
  
  // Use domainIconMapRaw only for domain-related icons
  if (category === 'domain') {
    return domainIconMapRaw[type] || defaultDomainIcon;
  }

  // For other categories, use the specific icons
  const iconMap = {
    bac: { 
      general: general_icon, 
      technologique: tech_icon, 
      professionnel: pro_icon 
    },
    user: {
      seconde: secondeIcon, 
      premiere: premiereIcon, 
      terminale: terminaleIcon,
      bac1: etudiantIcon, 
      parent: parentIcon, 
      autre: otherIcon
    },
    speciality: {
      arts: artsIcon, 
      eppcs: eppcsIcon, 
      hggsp: hggspIcon, 
      hlp: hlpIcon, 
      llcer: llcerIcon,
      lca: lcaIcon, 
      math: mathIcon, 
      nsi: nsiIcon, 
      physchim: physChimIcon, 
      svt: svtIcon,
      si: siIcon, 
      ses: sesIcon, 
      bioeco: bioEcoIcon, 
      general: moyenne_croissance_icon,
      amc: amcIcon
    },
    top_option: { 
      top1: top1Icon, 
      top5: top5Icon, 
      top_class: topClassIcon,
      top_classe: topClassIcon 
    }
  };

  return iconMap[category]?.[type] || defaultDomainIcon;
};


function UserProfilePage() {
    const { userId: profileUserIdFromParams } = useParams();
    const { user: requestUser, isAuthenticated, loading: authLoading, checkAuth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { isMobile } = useDeviceType();

    const [profileData, setProfileData] = useState(null); 
    const [socialProfileData, setSocialProfileData] = useState(null); 
    const [loadingData, setLoadingData] = useState(true); 
    const [error, setError] = useState('');

    const [isFollowing, setIsFollowing] = useState(false);
    const [isProcessingFollow, setIsProcessingFollow] = useState(false);
    const [pressedButton, setPressedButton] = useState(null);

    const [newPseudo, setNewPseudo] = useState('');
    const [isEditingPseudo, setIsEditingPseudo] = useState(false);
    const [showAllDomains, setShowAllDomains] = useState(false);
    const [expandedSpec, setExpandedSpec] = useState(null); 
    const [sliderValue, setSliderValue] = useState(10); 

    const [selectedProfilePictureFile, setSelectedProfilePictureFile] = useState(null);
    const [profilePicturePreviewUrl, setProfilePicturePreviewUrl] = useState(null);
    const [isUploadingProfilePicture, setIsUploadingProfilePicture] = useState(false);
    const [profilePictureUploadError, setProfilePictureUploadError] = useState('');
    const profilePictureInputRef = useRef(null);
    const [profilePicCacheKey, setProfilePicCacheKey] = useState(Date.now()); 

    const [activeStatBox, setActiveStatBox] = useState(null); 
    const [activeSwipeBox, setActiveSwipeBox] = useState(false); 

    // Formacode data states
    const [formacodeMap, setFormacodeMap] = useState(null); // Stores { formacodes: [], hierarchy: [] }
    const [hierarchyCodeToIconMap, setHierarchyCodeToIconMap] = useState(new Map());
    const [formacodeLoading, setFormacodeLoading] = useState(true);


    const isOwnProfileView = !profileUserIdFromParams || (isAuthenticated && requestUser?.id?.toString() === profileUserIdFromParams);
    const displayUserId = isOwnProfileView ? requestUser?.id : profileUserIdFromParams;

    // --- Load Formacode Data ---
    useEffect(() => {
        const loadFormacodes = async () => {
            setFormacodeLoading(true);
            try {
                const response = await fetch('/formacodes_recoded_optimized.csv');
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const csvData = await response.text();
                if (!csvData || csvData.trim() === "") throw new Error("CSV vide");

                const parsed = parseFormacodeData(csvData);
                setFormacodeMap(parsed);

                const newHierarchyMap = new Map();
                Object.entries(domainIconsFormacodesSousGrandDomaines).forEach(([domainName, icon]) => {
                    const formacodeEntry = parsed.formacodes.find(
                        fc => fc.descriptorRich === domainName &&
                              fc.hierarchyCode &&
                              fc.hierarchyCode.length === 4
                    );
                    if (formacodeEntry && formacodeEntry.hierarchyCode) {
                        newHierarchyMap.set(formacodeEntry.hierarchyCode, icon);
                    }
                });
                setHierarchyCodeToIconMap(newHierarchyMap);
            } catch (err) {
                console.error('Erreur chargement formacode:', err);
                // Handle error, maybe set an error state
            } finally {
                setFormacodeLoading(false);
            }
        };
        loadFormacodes();
    }, []);

    // --- Effect to fetch social profile data ---
    useEffect(() => {
        const loadSocialProfile = async () => {
            if (displayUserId) {
                setLoadingData(true);
                setError('');
                try {
                    const socialApiUrl = `${process.env.REACT_APP_BACKEND_URL}/social/profile/${displayUserId}/`;
                    const fetchedSocialData = await apiCall(socialApiUrl);
                    setSocialProfileData(fetchedSocialData);
                    setIsFollowing(fetchedSocialData?.is_followed_by_request_user || false);
                } catch (err) {
                    console.error("Error fetching social profile data:", err);
                    setError(err.response?.status === 404 ? 'Profil introuvable.' : 'Échec du chargement du profil.');
                    setSocialProfileData(null); // Clear previous data on error
                } finally {
                    setLoadingData(false);
                }
            } else if (isOwnProfileView && !authLoading && !requestUser) {
                // If we know it's our own profile but there's no user, it's an issue.
                setError("Données utilisateur non disponibles.");
                setLoadingData(false);
            }
        };

        if (authLoading) {
            setLoadingData(true);
            return;
        }

        if (isOwnProfileView && !isAuthenticated) {
            navigate('/login');
            return;
        }

        loadSocialProfile();
    }, [displayUserId, isOwnProfileView, isAuthenticated, authLoading, navigate]);

    // --- Effect to combine social data with authenticated user data ---
    useEffect(() => {
        if (isOwnProfileView && requestUser) {
            // It's our profile, combine the fetched social data with the more detailed requestUser data
            setProfileData(prevData => ({
                ...prevData, // a base of what might have been fetched socially
                ...socialProfileData, // override with latest social data
                ...requestUser, // override with the definitive authenticated user data
                id: requestUser.id,
                pseudo: requestUser.pseudo || socialProfileData?.pseudo,
                domaine_formation_codes: requestUser.domaine_formation_codes,
                sub_domaine_formation_codes: requestUser.sub_domaine_formation,
            }));
        } else {
            // It's someone else's profile, just use the social data
            setProfileData(socialProfileData);
        }
    }, [requestUser, socialProfileData, isOwnProfileView]);
    
    // --- Get Domain Details (name and icon) ---
    const getDomainDetails = (hierarchyCode) => {
        if (!hierarchyCode || !formacodeMap || formacodeLoading) {
            return { name: hierarchyCode || 'Chargement...', icon: defaultDomainIcon };
        }
    
        const formacodeEntry = formacodeMap.formacodes.find(fc => fc.hierarchyCode === hierarchyCode);
        
        if (formacodeEntry) {
            const icon = hierarchyCodeToIconMap.get(hierarchyCode) || 
                       (hierarchyCode.length > 4 ? hierarchyCodeToIconMap.get(hierarchyCode.substring(0,4)) : defaultDomainIcon) || 
                       defaultDomainIcon;
            return {
                name: formacodeEntry.descriptorRich || formacodeEntry.descriptor || 'Domaine inconnu',
                icon: icon
            };
        }
    
        if (hierarchyCode.length > 4) {
            const parentHierarchyCode = hierarchyCode.substring(0, 4);
            const parentIcon = hierarchyCodeToIconMap.get(parentHierarchyCode);
            const parentEntry = formacodeMap.formacodes.find(fc => fc.hierarchyCode === parentHierarchyCode);
            if (parentEntry) {
                return {
                    name: `Sous-domaine de ${parentEntry.descriptorRich || parentEntry.descriptor || 'Domaine parent'} (${hierarchyCode})`,
                    icon: parentIcon || defaultDomainIcon
                };
            }
        }
        return { name: hierarchyCode || 'Domaine inconnu', icon: defaultDomainIcon };
    };


    // --- Combine primary and secondary domains (hierarchy codes) for display ---
    const combinedUserDomainCodes = useMemo(() => {
        if (!profileData) return [];
        // Assuming profileData.domaine_formation and sub_domaine_formation now store hierarchy codes
        // const primaryCodes = profileData.domaine_formation_codes || []; // e.g., from requestUser.domaine_formation_codes
        const secondaryCodes = profileData.sub_domaine_formation_codes || []; // e.g., from requestUser.sub_domaine_formation_codes
        return secondaryCodes
    }, [profileData]);


    useEffect(() => {
      if (requestUser && isOwnProfileView) {
        setNewPseudo(requestUser.pseudo || '');
      }
    }, [requestUser, isOwnProfileView]);

    const handleFollowToggle = async () => {
        if (!isAuthenticated || !profileUserIdFromParams || isOwnProfileView) {
            if(!isAuthenticated) navigate('/login');
            return;
        }
        if (isProcessingFollow) return;
        setIsProcessingFollow(true); setError('');
        const previousIsFollowing = isFollowing;
        const currentFollowers = socialProfileData?.followers_count ?? 0;
        const adjustment = previousIsFollowing ? -1 : 1;
        const newFollowersCount = Math.max(0, currentFollowers + adjustment);

        const updatedSocialData = { ...socialProfileData, followers_count: newFollowersCount, is_followed_by_request_user: !previousIsFollowing };
        setSocialProfileData(updatedSocialData);
        if (profileData && profileData.id === socialProfileData?.id) { 
            setProfileData(prev => ({ ...prev, ...updatedSocialData }));
        }
        setIsFollowing(!previousIsFollowing);
        const action = previousIsFollowing ? 'unfollow' : 'follow';
        const url = `${process.env.REACT_APP_BACKEND_URL}/social/profile/${profileUserIdFromParams}/${action}/`;
        try {
            await apiCall(url, 'POST');
        } catch (err) {
            console.error(`Error ${action} user:`, err);
            setError(`Impossible de ${previousIsFollowing ? 'se désabonner de' : 'suivre'} l'utilisateur.`);
            const rollbackSocialData = { ...socialProfileData, followers_count: currentFollowers, is_followed_by_request_user: previousIsFollowing };
            setSocialProfileData(rollbackSocialData);
            if (profileData && profileData.id === socialProfileData?.id) {
                 setProfileData(prev => ({ ...prev, ...rollbackSocialData }));
            }
            setIsFollowing(previousIsFollowing);
        } finally {
            setIsProcessingFollow(false);
        }
    };

    const handlePressStart = (buttonName) => setPressedButton(buttonName);
    const handlePressEnd = () => setTimeout(() => setPressedButton(null), 100);
    const handleMouseLeave = () => setPressedButton(null);
    const handleBack = () => navigate(-1);
    const handleSettingsClick = () => navigate('/settings');

    const handleProfilePictureChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            if (profilePicturePreviewUrl) { 
                URL.revokeObjectURL(profilePicturePreviewUrl);
            }
            setSelectedProfilePictureFile(file);
            setProfilePicturePreviewUrl(URL.createObjectURL(file)); 
            setProfilePictureUploadError('');
            await handleSaveProfilePicture(file); 
        }
    };

    const triggerProfilePictureUpload = () => {
        if (isOwnProfileView) {
            profilePictureInputRef.current?.click();
        }
    };

    const handleSaveProfilePicture = async (fileToUpload) => {
        const currentFile = fileToUpload || selectedProfilePictureFile; 
        if (!currentFile || !isOwnProfileView) return;

        setIsUploadingProfilePicture(true);
        setProfilePictureUploadError('');
        const formData = new FormData();
        formData.append('profile_picture', currentFile);

        try {
            await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/upload-custom-picture/`, 'POST', formData);
            await checkAuth(); 
            setProfilePicCacheKey(Date.now()); 
        } catch (error) {
            console.error("Error uploading custom photo:", error);
            setProfilePictureUploadError("Erreur lors de l'envoi de la photo. Veuillez réessayer.");
            if (profilePicturePreviewUrl) {
                URL.revokeObjectURL(profilePicturePreviewUrl);
            }
            setProfilePicturePreviewUrl(null); 
        } finally {
            setIsUploadingProfilePicture(false);
            setSelectedProfilePictureFile(null); 
            if (!profilePictureUploadError) {
                 if (profilePicturePreviewUrl) URL.revokeObjectURL(profilePicturePreviewUrl);
                 setProfilePicturePreviewUrl(null);
            }
        }
    };

    // Updated to use domain codes and navigate to a page that can handle them
    const handleDomainClick = () => {
        const domainCodes = combinedUserDomainCodes;
        if (domainCodes?.length > 0) {
            if (isOwnProfileView) navigate('/profile/modify/domaines-favoris'); // This page should now handle codes
            // For public view, we might need a new route or pass codes differently if that page needs to display them
            // else navigate(`/profile/${displayUserId}/domaines-favoris`, { state: { domain_codes: domainCodes, pseudo: profileData.pseudo }});
            else console.log("Viewing other user's domains - navigation TBD for codes", domainCodes);
        }
    };
    
    const handleFilieresClick = () => navigate('/filieres-favorites');
    const handleSwipeBoxPressEnd = () => {
        setActiveSwipeBox(false);
        setTimeout(() => {
            navigate('/profile/modify/swipe');
        }, 100);
    };
    const handleOwnDomainsClick = () => navigate('/profile/modify/domaines-favoris'); // This page should also handle codes

    const handleVoeuxPressEnd = () => {
        setActiveStatBox(null);
        setTimeout(() => {
            navigate('/formations-favorites');
        }, 100); 
    };

    const handleUpdatePseudo = async () => {
        if (!newPseudo || !isOwnProfileView || !requestUser) return;
        try {
          await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', { pseudo: newPseudo });
          await checkAuth(); 
          setIsEditingPseudo(false);
          setProfileData(prev => ({...prev, pseudo: newPseudo})); 
        } catch (error) {
          console.error('Error updating pseudo:', error);
          setError("Erreur lors de la mise à jour du pseudo.");
        }
    };

    const handleScoreClick = (e, spec) => {
        e.stopPropagation();
        if (expandedSpec === spec) {
          setExpandedSpec(null);
        } else {
          const currentGrade = profileData?.grades?.[spec] ?? 10;
          setSliderValue(currentGrade);
          setExpandedSpec(spec);
        }
    };

    const handleScoreConfirm = async (spec) => {
        if (!isOwnProfileView || !requestUser) return;
        try {
          const newGrades = { ...requestUser.grades, [spec]: sliderValue };
          await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', { grades: newGrades });
          await checkAuth(); 
          setProfileData(prev => ({...prev, grades: newGrades })); 
          setExpandedSpec(null);
          localStorage.setItem('need_update_ranking', 'true');
          localStorage.setItem('need_update_ranking_formation', 'true');
        } catch (error) {
          console.error('Error updating grade:', error);
          setError("Erreur lors de la mise à jour de la note.");
        }
    };

    if (authLoading) return <LoadingErrorContainer>Chargement du profil...</LoadingErrorContainer>;
    if (isOwnProfileView && !isAuthenticated) return <LoadingErrorContainer>Veuillez vous connecter.</LoadingErrorContainer>;
    if (loadingData && !isOwnProfileView) return <LoadingErrorContainer>Chargement du profil...</LoadingErrorContainer>;
    if (error) return <ProfileContainer><LoadingErrorContainer>{error}</LoadingErrorContainer></ProfileContainer>;
    if (!profileData && !isOwnProfileView) return <ProfileContainer><LoadingErrorContainer>Profil non disponible.</LoadingErrorContainer></ProfileContainer>;
    
    const dp = profileData || requestUser; 
    if (!dp) return <ProfileContainer><LoadingErrorContainer>Profil non disponible.</LoadingErrorContainer></ProfileContainer>;

    let pictureSrc = defaultAvatar;
    if (profilePicturePreviewUrl) {
        pictureSrc = profilePicturePreviewUrl;
    } else if (dp.instagram_profile_picture_url) {
        const encodedUrl = encodeURIComponent(dp.instagram_profile_picture_url);
        pictureSrc = `${process.env.REACT_APP_BACKEND_URL}/social/image-proxy/?url=${encodedUrl}`;
    } else if (dp.custom_profile_picture_url) {
        let baseUrl = dp.custom_profile_picture_url;
        if (baseUrl.startsWith('/')) { 
            baseUrl = `${process.env.REACT_APP_BACKEND_URL}${baseUrl}`;
        }
        pictureSrc = `${baseUrl}?v=${profilePicCacheKey}`;
    } else if (dp.profile_picture_url) {
        if (dp.profile_picture_url.startsWith('/')) { 
            pictureSrc = `${process.env.REACT_APP_BACKEND_URL}${dp.profile_picture_url}`;
        } else {
            pictureSrc = dp.profile_picture_url;
        }
    }

    const showFollowBtn = !isOwnProfileView && isAuthenticated;
    const showInstaBtn = !!dp.instagram_username && !isOwnProfileView;
    const showTopSettingsBtn = isOwnProfileView && isMobile;
    const showEditProfileBtn = isOwnProfileView; // Not directly used for a button, but implies editability

    const areBothActionButtonsVisible = showFollowBtn && showInstaBtn;

    const topBarDomainCodes = combinedUserDomainCodes;
    const topBarDomainCount = topBarDomainCodes.length;
    let topBarFirstDomainIconDetails = getDomainDetails(topBarDomainCodes[0]); // Gets {name, icon}
    let topBarAdditionalDomainsCount = 0;

    if (topBarDomainCount > 0) {
        topBarAdditionalDomainsCount = topBarDomainCount - 1;
    } else {
        topBarFirstDomainIconDetails = { name: 'Aucun domaine', icon: defaultDomainIcon };
    }


    const OwnProfileContentComponent = () => {
        if (!isOwnProfileView || !dp) return null;
        const formationsCount = dp.formations?.length || 0;
        // const typesCount = dp.types_formations?.length || 0; // Not used
        // const messagesCount = dp.number_of_messages_sent || 0; // Not used
        // const cardsVisited = dp.number_of_cards_visited || 0; // Not used
        
        const allUserDomainCodes = combinedUserDomainCodes;
        const displayedDomainCodes = showAllDomains ? allUserDomainCodes : allUserDomainCodes.slice(0, 2);
        const userSpecialites = dp.specialites || [];
        const specialtiesWithGeneral = Array.isArray(userSpecialites) ? [...userSpecialites, 'general'] : ['general'];

        return (
            <div style={{width: '100%', maxWidth: '600px', display:'flex', flexDirection:'column', alignItems:'center', gap: '20px'}}>
                <RecapContainer>
                    <StatBox 
                        style={{
                            borderRadius: '15px', 
                            background: '#38B6FF', 
                            boxShadow: activeStatBox === 'voeux' ? 'none' : '0px 2px 0px 0px #1E99E0', 
                            cursor: 'pointer',
                            transform: activeStatBox === 'voeux' ? 'translateY(2px)' : 'translateY(0)',
                            transition: 'all 0.15s ease'
                        }}
                        onMouseDown={() => setActiveStatBox('voeux')}
                        onMouseUp={handleVoeuxPressEnd}
                        onMouseLeave={() => setActiveStatBox(null)} 
                        onTouchStart={() => setActiveStatBox('voeux')}
                        onTouchEnd={handleVoeuxPressEnd}
                    >
                        <StatNumber>{formationsCount}</StatNumber>
                        <RecapStatLabel>{formationsCount === 1 ? 'voeu' : 'voeux'}</RecapStatLabel>
                        <HeartIcon src={coeurBlancIcon} alt="Heart" />
                    </StatBox>
                    <StatBox 
                        style={{
                            borderRadius: '15px',
                            background: '#53D13D',
                            boxShadow: activeStatBox === 'visites' ? 'none' : '0px 2px 0px 0px #45B831',
                            cursor: 'pointer', 
                            transform: activeStatBox === 'visites' ? 'translateY(2px)' : 'translateY(0)',
                            transition: 'all 0.15s ease'
                        }}
                        onMouseDown={() => setActiveStatBox('visites')}
                        onMouseUp={() => {
                            setActiveStatBox(null);
                            navigate('/lettres');
                        }}
                        onMouseLeave={() => setActiveStatBox(null)}
                        onTouchStart={() => setActiveStatBox('visites')}
                        onTouchEnd={() => {
                            setActiveStatBox(null);
                            navigate('/lettres');
                        }}
                    >
                        <StatNumber>{(() => {
                            try {
                                const storedData = localStorage.getItem('lettresData');
                                if (storedData) {
                                    const parsedData = JSON.parse(storedData);
                                    return Object.entries(parsedData).filter(([_, data]) => 
                                        data.letterContent || (data.questions && data.questions.every(q => q.answer && q.answer.trim() !== ''))
                                    ).length;
                                }
                            } catch (error) {
                                console.error('Error loading letters data:', error);
                            }
                            return 0;
                        })()}</StatNumber>
                        <RecapStatLabel>lettres</RecapStatLabel>
                    </StatBox>
                </RecapContainer>

                <RubriqueTitle>Quiz</RubriqueTitle>
                <SwipeSection>
                  <SwipeBox 
                    onClick={null} /* Click is handled by mouseup/touchend */
                    onMouseDown={() => setActiveSwipeBox(true)}
                    onMouseUp={handleSwipeBoxPressEnd}
                    onMouseLeave={() => setActiveSwipeBox(false)}
                    onTouchStart={() => setActiveSwipeBox(true)}
                    onTouchEnd={handleSwipeBoxPressEnd}
                    isActive={activeSwipeBox}
                  >
                    <QuizIcon src={quizBuzzIconAsset} />
                    <SwipeText>Voir mes réponses</SwipeText>
                  </SwipeBox>
                </SwipeSection>

                <RubriqueTitle>Mes domaines favoris</RubriqueTitle>
                <BoxContainer onClick={handleOwnDomainsClick} style={{cursor: 'pointer'}}>
                    {displayedDomainCodes.length === 0 && !formacodeLoading && (
                        <ItemBox style={{ justifyContent: 'center' }}>
                            <ItemText>Aucun domaine favori sélectionné.</ItemText>
                        </ItemBox>
                    )}
                    {displayedDomainCodes.map((code, idx) => {
                        const domainDetails = getDomainDetails(code);
                        return (
                            <React.Fragment key={code + idx}>
                            <ItemBox>
                                <ItemIcon bgImage={domainDetails.icon} /> {/* Use bgImage prop for styled component if it expects that */}
                                <ItemText>{domainDetails.name}</ItemText>
                            </ItemBox>
                            {idx < displayedDomainCodes.length - 1 && <ItemSeparator />}
                            </React.Fragment>
                        );
                    })}
                    {allUserDomainCodes.length > 2 && (
                        <><ItemSeparator /><VoirPlusButton onClick={(e) => { e.stopPropagation(); setShowAllDomains(!showAllDomains);}}>{showAllDomains ? 'Voir moins' : 'Voir plus...'}</VoirPlusButton></>
                    )}
                </BoxContainer>
                
                <RubriqueTitle>Mes spécialités</RubriqueTitle>
                <BoxContainer>
                    {specialtiesWithGeneral.map((spec, idx) => {
                        const currentGrade = dp.grades?.[spec] ?? '?';
                        // Assuming getIconForType can handle 'speciality' type with the spec value
                        const specIcon = getIconForType(spec, 'speciality') || defaultDomainIcon; 
                        return (
                        <React.Fragment key={spec + idx}>
                            <ItemBox onClick={() => navigate('/profile/modify/specialites')} style={{cursor: 'pointer'}}>
                                <ItemIcon bgImage={specIcon} />
                                <ItemText>{getSpecialityLabel(spec)}</ItemText>
                                <ScoreBox onClick={(e) => handleScoreClick(e, spec)}><ScoreText>{currentGrade}/20</ScoreText></ScoreBox>
                            </ItemBox>
                            {expandedSpec === spec && (
                            <SliderContainer>
                                <SliderValue>{sliderValue}</SliderValue>
                                <GradeSlider type="range" min="0" max="20" step="0.5" value={sliderValue} onChange={(e) => setSliderValue(Number(e.target.value))} />
                                <ConfirmGradeButton onClick={() => handleScoreConfirm(spec)}>Valider</ConfirmGradeButton>
                            </SliderContainer>
                            )}
                            {idx < specialtiesWithGeneral.length - 1 && <ItemSeparator />}
                        </React.Fragment>
                        );
                    })}
                </BoxContainer>

                <RubriqueTitle>Mes infos</RubriqueTitle>
                <BoxContainer>
                    <ItemBox onClick={() => navigate('/profile/modify/type-bac')} style={{cursor: 'pointer'}}>
                        <ItemIcon bgImage={getIconForType(dp.type_bac, 'bac')} /><ItemText>{getBacLabel(dp.type_bac) || 'N/A'}</ItemText>
                    </ItemBox>
                    <ItemSeparator />
                    <ItemBox onClick={() => navigate('/profile/modify/user-type')} style={{cursor: 'pointer'}}>
                        <ItemIcon bgImage={getIconForType(dp.user_type, 'user')} /><ItemText>{getUserTypeLabel(dp.user_type) || 'N/A'}</ItemText>
                    </ItemBox>
                    <ItemSeparator />
                    <ItemBox>
                        <ItemIcon bgImage={pseudoIconAsset} />
                        {isEditingPseudo ? (
                            <div style={{display: 'flex', alignItems: 'center', width: '100%', gap: '10px'}}>
                                <PseudoTextArea value={newPseudo} onChange={(e) => setNewPseudo(e.target.value)} placeholder="Votre pseudo"/>
                                <ConfirmButton onClick={handleUpdatePseudo}>OK</ConfirmButton>
                                <button onClick={() => setIsEditingPseudo(false)} style={{background:'none', border:'none', cursor:'pointer', fontSize: '16px', color: theme.colors.textLight}}>X</button>
                            </div>
                        ) : (
                            <ItemText onClick={() => setIsEditingPseudo(true)} style={{cursor: 'pointer'}}>{dp.pseudo || 'N/A'}</ItemText>
                        )}
                    </ItemBox>
                    <ItemSeparator />
                    <ItemBox onClick={() => navigate('/profile/modify/classe-ment')} style={{cursor: 'pointer'}}>
                        <ItemIcon bgImage={getIconForType(dp.top_option, 'top_option')} />
                        <ItemText>{getUserTypeLabel(dp.top_option) || 'Classement non renseigné'}</ItemText>
                    </ItemBox>
                </BoxContainer>
            </div>
        );
    };

    return (
        <ProfileContainer isPublicProfile={!isOwnProfileView}>
            {!isOwnProfileView && <BackButtonStyled src={backIcon} alt="Retour" onClick={handleBack} />}
            {showTopSettingsBtn && (
                <button onClick={handleSettingsClick} style={{position: 'absolute', top: '20px', right: '20px', background:'none', border:'none', fontSize:'24px', cursor:'pointer', zIndex: 3002, width: '24px', height: '24px', padding: '0'}}>
                    <img src={settingsGrisIcon} alt="Settings" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain'}} />
                </button>
            )}
            <ProfileHeader>
                <ProfilePicture 
                    src={pictureSrc} 
                    alt={`${dp.pseudo || 'User'}'s profile`} 
                    onClick={isOwnProfileView && !isUploadingProfilePicture ? triggerProfilePictureUpload : undefined}
                    style={isOwnProfileView ? { cursor: isUploadingProfilePicture ? 'default' : 'pointer', position: 'relative' } : {position: 'relative'}}
                >
                    {isOwnProfileView && isUploadingProfilePicture && (
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(255,255,255,0.7)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', borderRadius: '50%'
                        }}>
                            <div style={{
                                border: '4px solid #f3f3f3', borderTop: `4px solid ${theme.colors.primary || '#007bff'}`,
                                borderRadius: '50%', width: '30px', height: '30px',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                             {/* Ensure @keyframes spin is defined globally or in styles */}
                        </div>
                    )}
                </ProfilePicture>
                {isOwnProfileView && (
                    <input
                        type="file"
                        ref={profilePictureInputRef}
                        onChange={handleProfilePictureChange}
                        accept="image/png, image/jpeg, image/gif"
                        style={{ display: 'none' }}
                    />
                )}
                {isOwnProfileView && profilePictureUploadError && 
                    <ErrorText style={{color: theme.colors.rouge || 'red', marginTop: '5px'}}>{profilePictureUploadError}</ErrorText>
                }
                <Username>{dp.pseudo || (isOwnProfileView ? 'Mon Profil' : 'Utilisateur anonyme')}</Username>
            </ProfileHeader>
            <StatsContainer>
                 <SocialStatItem clickable={topBarDomainCount > 0 && !formacodeLoading} onClick={handleDomainClick}>
                    <SocialDomainDisplayWrapper>
                        <SocialDomainIcon src={topBarFirstDomainIconDetails.icon} />
                        {topBarAdditionalDomainsCount > 0 && <SocialDomainCounter>+{topBarAdditionalDomainsCount}</SocialDomainCounter>}
                    </SocialDomainDisplayWrapper>
                    <SocialStatLabel>Domaines</SocialStatLabel>
                </SocialStatItem>
                <VerticalSeparator />
                <SocialStatItem clickable={true} onClick={isOwnProfileView ? ()=>navigate('/profile/me/following') : () => navigate(`/profile/${profileUserIdFromParams}/following`)}><SocialStatValue>{dp.following_count ?? 0}</SocialStatValue><SocialStatLabel>Abonnements</SocialStatLabel></SocialStatItem>
                <VerticalSeparator />
                <SocialStatItem clickable={true} onClick={isOwnProfileView ? ()=>navigate('/profile/me/followers') : () => navigate(`/profile/${profileUserIdFromParams}/followers`)}><SocialStatValue>{dp.followers_count ?? 0}</SocialStatValue><SocialStatLabel>Abonnés</SocialStatLabel></SocialStatItem>
            </StatsContainer>
            <ButtonContainer>
                {showFollowBtn && (<PrimaryActionButton onClick={handleFollowToggle} disabled={isProcessingFollow} onMouseDown={() => handlePressStart('follow')} onMouseUp={handlePressEnd} onMouseLeave={handleMouseLeave} onTouchStart={() => handlePressStart('follow')} onTouchEnd={handlePressEnd} isActive={pressedButton === 'follow'}>{isFollowing ? (<ButtonIcon src={abonneIcon} alt="Abonné(e)" />) : (<><ButtonIcon src={abonnementBlancIcon} alt="S'abonner" />Suivre</>)}</PrimaryActionButton>)}
                {showInstaBtn && (<SecondaryActionButton as="a" href={`https://instagram.com/${dp.instagram_username}`} target="_blank" rel="noopener noreferrer" onMouseDown={() => handlePressStart('instagram')} onMouseUp={handlePressEnd} onMouseLeave={handleMouseLeave} onTouchStart={() => handlePressStart('instagram')} onTouchEnd={handlePressEnd} isActive={pressedButton === 'instagram'}><ButtonIcon src={instaBleuIcon} alt="Instagram" /></SecondaryActionButton>)}
            </ButtonContainer>
            <InfoSection areBothActionButtonsVisible={areBothActionButtonsVisible}>
                 <SocialInfoBox onClick={isOwnProfileView ? () => navigate('/profile/modify/formation') : undefined} style={isOwnProfileView ? {cursor: 'pointer'} : {}}>
                     <InfoIconStyled src={locationIcon} alt="Formation" />
                     <SocialInfoTextContainer>
                         {dp.personal_formation ? (
                             <>
                                 <SocialInfoTitle>{dp.personal_formation}</SocialInfoTitle>
                                 <SocialInfoSubtitle>{dp.current_establishment || 'Établissement non spécifié'}</SocialInfoSubtitle>
                             </>
                         ) : isOwnProfileView ? (
                             <SocialInfoTitle>Rajouter ma formation</SocialInfoTitle>
                         ) : (
                             <SocialInfoSubtitle>Abonne-toi pour être au courant</SocialInfoSubtitle>
                         )}
                     </SocialInfoTextContainer>
                 </SocialInfoBox>
                 {(isOwnProfileView || dp.lycee_name) && (
                     <SocialInfoBox 
                         onClick={isOwnProfileView ? () => navigate('/profile/modify/lycee') : undefined} 
                         style={isOwnProfileView ? {cursor: 'pointer'} : {}}
                     >
                         <InfoIconStyled src={schoolIconAsset} alt="Lycée" />
                         <SocialInfoTextContainer>
                             {dp.lycee_name ? (
                                 <>
                                     <SocialInfoTitle>{dp.lycee_name}{dp.lycee_city ? `, ${dp.lycee_city}` : ''}</SocialInfoTitle>
                                     <SocialInfoSubtitle>Lycée</SocialInfoSubtitle>
                                 </>
                             ) : (
                                 <SocialInfoTitle>Mon école</SocialInfoTitle>
                             )}
                         </SocialInfoTextContainer>
                     </SocialInfoBox>
                 )}
            </InfoSection>
            {isOwnProfileView && <OwnProfileContentComponent />}
        </ProfileContainer>
    );
}

export default UserProfilePage;